import moment from 'moment-timezone'
import React, { createContext } from 'react'

import { FunctionMode, Message, SearchProcessItem, SelectableModel } from '@/types/message.type'
import { alphaPath } from '@/api/config'
import { ModalProps, ShareChannel } from '@/types'
import { DateFilterOption } from '@/types/filter.type'
import { OwnershipTransactionHistoryData } from '@/types/ownership_data.type'
import {
  NewsReference,
  Reference,
  ReferenceDocumentType,
  ReferenceMode,
} from '@/types/reference.type'
import { REFERENCE_IDX_REGEX } from '@/constants'
import { RmsReference } from '@/types/rms_reference.type'
import { InferenceReference } from '@/types/inference.type'
import { ColumnType } from '@/types/table.type'
import { PresignedUrlMode, RmsV2Reference } from '@/types/rms_v2.type'
import { CurrencyInfo, FieldCurrencyInfo } from '@/constants/company_page'

export const TFS_MAX_SELECTED_TICKERS = 10

export function insertTextAtCursor(element: HTMLTextAreaElement, textToInsert: string) {
  element.focus()

  // Use the browser's built-in undoable actions if possible
  if (window.getSelection() && document.queryCommandSupported('insertText')) {
    document.execCommand('insertText', false, textToInsert)
  } else {
    console.warn('insertTextAtCursor: document.execCommand is not supported')
    const startPos = element.selectionStart
    const endPos = element.selectionEnd
    const beforeText = element.value.substring(0, startPos)
    const afterText = element.value.substring(endPos)
    element.value = beforeText + textToInsert + afterText
    element.selectionStart = element.selectionEnd = startPos + textToInsert.length
    const event = new Event('input', { bubbles: true })
    element.dispatchEvent(event)
  }
}

export const forceResize = (textAreaRef: React.RefObject<HTMLTextAreaElement>) => {
  if (!textAreaRef.current) {
    return
  }
  textAreaRef.current.style.height = 'auto'
  textAreaRef.current.offsetHeight
  textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`
}

export function fromNextUrl(url = '') {
  url = url || ''

  if (url.indexOf('___') === 0) {
    const tmpUrl = url.split('___')
    url = tmpUrl[tmpUrl.length - 1]
    return atob(url)
  }

  return url
}

export const createUrlWithUtm = (
  baseUrl: string,
  channel: ShareChannel,
  utm_medium?: string,
  utm_campaign?: string
) => {
  const utmParams = new URLSearchParams({
    utm_source: channel,
    utm_medium: utm_medium || 'social',
    utm_campaign: utm_campaign || 'share_modal',
  })
  return `${baseUrl}?${utmParams.toString()}`
}

export const getSSEUrl = (model: SelectableModel, functionMode?: FunctionMode) => {
  switch (model) {
    case SelectableModel.LinqAlpha:
      return `${process.env.BACKEND_API_URL}${alphaPath}/sse/alpha_completion`
    case SelectableModel.IntegrateSearch:
      if (functionMode === FunctionMode.Normal) {
        return `${process.env.BACKEND_API_URL}${alphaPath}/sse/integrate_alpha_completion`
      } else if (functionMode === FunctionMode.CallSummarize) {
        return `${process.env.BACKEND_API_URL}${alphaPath}/sse/call_summarize`
      } else if (functionMode === FunctionMode.StructuredData) {
        return `${process.env.BACKEND_API_URL}${alphaPath}/sse/structured_data`
      }
      return `${process.env.BACKEND_API_URL}${alphaPath}/sse/integrate_alpha_completion`
    case SelectableModel.IntegrateSearchV2:
      if (functionMode === FunctionMode.Normal) {
        return `${process.env.BACKEND_API_URL}${alphaPath}/sse/integrate_alpha_completion`
      } else if (functionMode === FunctionMode.CallSummarize) {
        return `${process.env.BACKEND_API_URL}${alphaPath}/sse/call_summarize`
      } else if (functionMode === FunctionMode.StructuredData) {
        return `${process.env.BACKEND_API_URL}${alphaPath}/sse/structured_data`
      }
      return `${process.env.BACKEND_API_URL}${alphaPath}/sse/integrate_alpha_completion`
    case SelectableModel.News:
      return `${process.env.BACKEND_API_URL}${alphaPath}/sse/news_completion`
    case SelectableModel.GPT4o:
      return `${process.env.BACKEND_API_URL}${alphaPath}/sse/single_completion`
    case SelectableModel.RMS:
      return `${process.env.BACKEND_API_URL}${alphaPath}/sse/rms_completion`
    case SelectableModel.RMSPro:
      return `${process.env.BACKEND_API_URL}${alphaPath}/sse/rms_pro_completion`
    case SelectableModel.CallSummarizer:
      return `${process.env.BACKEND_API_URL}${alphaPath}/sse/call_summarize`
    case SelectableModel.ReasoningR1:
      return `${process.env.ONYX_CHAT_API_URL}/chat/send-message`
    case SelectableModel.RMSV2:
      return `${process.env.ONYX_CHAT_API_URL}/chat/send-message`
    default:
      return `${process.env.BACKEND_API_URL}${alphaPath}/sse/single_completion`
  }
}

export function truncate(input: unknown, maxLength: number, suffix = '...'): string {
  const str = input == null ? '' : String(input)
  if (str.length <= maxLength) {
    return str
  }
  return str.slice(0, maxLength - suffix.length) + suffix
}

export const referenceIdxConverter = (referenceIdx: string) => {
  return referenceIdx.split(',').flatMap((ref: string) => {
    // 일반 하이픈과 엔 대시 모두 체크
    if (ref.includes('-') || ref.includes('–')) {
      // 엔 대시도 일반 하이픈으로 정규화
      const normalizedRef = ref.replace('–', '-')
      const [start, end] = normalizedRef.split('-').map(Number)
      return Array.from({ length: end - start + 1 }, (_, i) => start + i)
    } else {
      return [Number(ref)]
    }
  })
}

// 한 셀 전체(trim 후)가 인용 마커(`[N]` / `[N,M]` / `[N-M]`) 하나 이상으로만 이뤄졌는지.
const CITATION_ONLY_CELL = /^(?:\[\s*[\d,\s\-–]+\s*\]\s*)+$/

// `| a | b |` 행을 셀 배열로 분해한다. 앞뒤 파이프를 제거하고, escape 된 `\|` 는 나누지 않는다.
// 이스케이프되지 않은 `|` 에서만 나눈다 — 원래 `(?<!\\)\|` 와 동일하지만 lookbehind 없이
// 구현한다. Safari 16.4 미만은 lookbehind 정규식을 파싱조차 못 해 utils.ts 청크 전체가
// 죽고 MarkdownViewer 가 빈 화면이 된다 (browserslist 미지정 → Next 기본값이 Safari 12 포함).
const splitTableRowCells = (line: string): string[] => {
  let s = line.trim()
  if (s.startsWith('|')) s = s.slice(1)
  if (s.endsWith('|')) s = s.slice(0, -1)
  const cells: string[] = []
  let start = 0
  for (let i = 0; i < s.length; i++) {
    if (s[i] === '|' && s[i - 1] !== '\\') {
      cells.push(s.slice(start, i).trim())
      start = i + 1
    }
  }
  cells.push(s.slice(start).trim())
  return cells
}

// GFM delimiter row (`| --- | :--: |` 등): 모든 셀이 dash(+optional colon) 로만 구성.
const isTableDelimiterRow = (line: string): boolean => {
  if (!line.trim().startsWith('|')) return false
  const cells = splitTableRowCells(line)
  return cells.length > 0 && cells.every((c) => /^:?-+:?$/.test(c))
}

// remarkGfm 은 헤더 열 수를 넘는 셀을 파싱 단계에서 드롭한다. 그래서 모델이 표 셀 뒤에
// 인용 마커를 별도 열로 흘려 넣으면(예: `... 한글 | [3] |`) 그 `[3]` 이 통째로 사라져
// 클릭 뱃지가 안 붙는다. 헤더 폭을 넘긴 "인용 마커만 든" trailing overflow 셀을 마지막
// 실제 셀 끝에 병합해 마커를 살린다. 폭이 맞는 표·인라인 인용·비표 텍스트·코드펜스는
// 건드리지 않는다(현재 깨진 overflow 행만 바뀐다).
export const mergeTableCitationOverflow = (text: string): string => {
  const lines = text.split('\n')
  let inFence = false
  let inTable = false
  let headerCols = 0
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    if (/^\s*(```|~~~)/.test(line)) {
      inFence = !inFence
      inTable = false
      continue
    }
    if (inFence) continue
    if (!inTable) {
      // 표 시작: 헤더 행 바로 다음 줄이 delimiter 행일 때.
      if (
        line.trim().startsWith('|') &&
        i + 1 < lines.length &&
        isTableDelimiterRow(lines[i + 1])
      ) {
        inTable = true
        headerCols = splitTableRowCells(line).length
      }
      continue
    }
    if (!line.trim().startsWith('|')) {
      inTable = false
      continue
    }
    if (isTableDelimiterRow(line) || headerCols < 1) {
      continue
    }
    const cells = splitTableRowCells(line)
    if (cells.length <= headerCols) {
      continue
    }
    const overflow = cells.slice(headerCols)
    if (!overflow.every((c) => CITATION_ONLY_CELL.test(c))) {
      continue // overflow 가 인용 마커만은 아님 → 정직한 데이터일 수 있으니 그대로 둔다
    }
    const kept = cells.slice(0, headerCols)
    kept[headerCols - 1] = `${kept[headerCols - 1]} ${overflow.join(' ')}`.trim()
    lines[i] = `| ${kept.join(' | ')} |`
  }
  return lines.join('\n')
}

// `[N]` 패턴을 reference 버튼용 `<a href="#">N</a>` 로 치환한다.
// 기본: bracket 안의 idx 가 references 에 모두 존재할 때만 변환하고, 하나라도 없으면 원문 유지.
// skipBoundsCheck=true: references 매칭 무시하고 모든 [N] 을 항상 링크로 변환 (예: company deepdive
// 처럼 reference 매칭이 적용되지 않는 화면에서 [N] 을 그대로 텍스트로 두면 안 되는 경우).
// fenced ```...``` 와 inline `code` 안의 [N] 은 보존한다 (렌더 시 literal HTML 노출 방지).
export const transformReferencesToLinks = (
  rawText: string,
  references: ReadonlyArray<{ idx: number }> = [],
  { skipBoundsCheck = false }: { skipBoundsCheck?: boolean } = {}
): string => {
  // GFM 이 마커를 드롭하기 전에, 표 overflow 로 새어 나간 인용 마커를 셀 안으로 되돌린다.
  const text = mergeTableCitationOverflow(rawText)
  const validIdxSet = new Set(references.map((r) => r.idx))
  const codeRegex = /```[\s\S]*?```|`[^`\n]+`/g

  const applyRefs = (segment: string): string =>
    segment.replace(REFERENCE_IDX_REGEX, (match, p1) => {
      const refs = referenceIdxConverter(p1)
      if (refs.length === 0) {
        return match
      }
      if (!skipBoundsCheck && refs.some((ref) => !validIdxSet.has(ref))) {
        return match
      }
      return refs.map((ref) => `<a href="#">${ref}</a>`).join(' ')
    })

  const out: string[] = []
  let cursor = 0
  let m: RegExpExecArray | null
  while ((m = codeRegex.exec(text)) !== null) {
    out.push(applyRefs(text.slice(cursor, m.index)))
    out.push(m[0])
    cursor = m.index + m[0].length
  }
  out.push(applyRefs(text.slice(cursor)))
  return out.join('')
}

export function deepEqual<T>(obj1: T, obj2: T): boolean {
  if (obj1 === obj2) {
    return true // 같은 참조
  }

  if (obj1 == null || obj2 == null || typeof obj1 !== 'object' || typeof obj2 !== 'object') {
    return false // null이거나 객체가 아닌 경우
  }

  const keys1 = Object.keys(obj1) as Array<keyof T>
  const keys2 = Object.keys(obj2) as Array<keyof T>

  if (keys1.length !== keys2.length) {
    return false // 키의 길이가 다른 경우
  }

  for (const key of keys1) {
    if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) {
      return false // 키가 존재하지 않거나 값이 다른 경우
    }
  }

  return true
}

export function expiresDate(sent_at: string) {
  const date = new Date(sent_at)

  const daysToAdd = 5
  const newDate = new Date(date.getTime() + daysToAdd * 24 * 60 * 60 * 1000)

  // 새로운 날짜를 ISO 문자열로 출력
  return newDate.toISOString()
}

export function pastTimeDisplay(time: string) {
  const now = moment()
  const timeInterval = moment(time).unix()
  const nowTimeInterval = now.unix()
  const diff = nowTimeInterval - timeInterval
  const year = Math.floor(diff / (60 * 60 * 24 * 365))
  if (year > 0) return `${year} years ago`

  const month = Math.floor(diff / (60 * 60 * 24 * 30))
  if (month > 0) return `${month} months ago`

  const week = Math.floor(diff / (60 * 60 * 24 * 7))
  if (week > 0) return `${week} weeks ago`

  const day = Math.floor(diff / (60 * 60 * 24))
  if (day > 0) return `${day} days ago`

  const hour = Math.floor(diff / (60 * 60))
  if (hour > 0) return `${hour} hours ago`

  const min = Math.floor(diff / 60)
  if (min > 0) return `${min} minutes ago`

  const sec = Math.floor(diff % 60)
  if (sec < 10) return 'just now'

  return `${sec} seconds ago`
}

export const DefaultModalContext = createContext<{
  openModal: (modalProps: ModalProps) => void
  closeModal: () => void
}>({
  openModal: () => {},
  closeModal: () => {},
})

export const truncateString = (target: string, maxLength: number) => {
  if (target.length > maxLength) {
    return target.slice(0, maxLength) + '...'
  } else {
    return target
  }
}

export const formatDateString = (dateString: string): string => {
  // 상대적 날짜 표현 처리
  const relativeDatePattern =
    /(\d+)\s+(days|weeks|months|day|week|month|hours|minutes|seconds|hour|minute|second)\s+Ago/i
  const match = dateString.match(relativeDatePattern)

  let date: Date

  if (match) {
    const value = parseInt(match[1], 10)
    const unit = match[2].toLowerCase()

    date = new Date()

    switch (unit) {
      case 'days':
        date.setDate(date.getDate() - value)
        break
      case 'weeks':
        date.setDate(date.getDate() - value * 7)
        break
      case 'months':
        date.setMonth(date.getMonth() - value)
        break
      case 'years':
        date.setFullYear(date.getFullYear() - value)
        break
    }
  } else {
    // 이미 포맷된 날짜 문자열인 경우
    date = new Date(dateString)
  }

  if (isNaN(date.getTime())) {
    return '' // Invalid Date인 경우 빈 문자열 반환
  }

  // 날짜 포맷: Jul 31, 2024
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }
  return date.toLocaleDateString('en-US', options)
}

export const transformDateFilter = (input: string) => {
  const date = moment.utc() // UTC 기준으로 현재 날짜 생성

  switch (input) {
    case DateFilterOption.Today:
      return { s_date: date.subtract(1, 'days').format('YYYY-MM-DD') }

    case DateFilterOption.PastWeek: {
      const s_date = date.clone().subtract(7, 'days').format('YYYY-MM-DD')
      const e_date = date.format('YYYY-MM-DD')
      return { s_date, e_date }
    }

    case DateFilterOption.PastMonth: {
      const s_date = date.clone().subtract(30, 'days').format('YYYY-MM-DD')
      const e_date = date.format('YYYY-MM-DD')
      return { s_date, e_date }
    }

    default:
      return null
  }
}

export const localDate = (date: Date) => {
  return moment(date).local().format('MMMM D, YYYY · h:mm A ([GMT]ZZ)')
}

/**
 * UTC 타임스탬프를 로컬 시간대로 변환하여 포맷팅된 문자열 반환
 * @param utcTimestamp - UTC 타임스탬프 문자열
 * @param format - moment 포맷 문자열 (선택사항)
 * @returns 포맷팅된 로컬 시간 문자열 또는 빈 문자열
 */
export const formatUtcToLocal = (
  utcTimestamp: string | undefined | null,
  format = 'ddd, MMM DD, YYYY [at] h:mm A'
): string => {
  if (!utcTimestamp) return ''

  return moment.utc(utcTimestamp).local().format(format)
}

export const earningsMarkdownConverter = (markdown: string) => {
  return markdown
    .replace('% beat', '%<span class="beat-highlight"> beat</span>')
    .replace('% miss', '%<span class="miss-highlight"> miss</span>')
    .replace('<u>beat</u>', '<u class="beat-highlight">beat</u>')
    .replace('<u>missed</u>', '<u class="miss-highlight">missed</u>')
}

export function getQuarterDates(year: number, quarter: number) {
  let startDate: Date | null = null
  let endDate: Date | null = null

  switch (quarter) {
    case 1:
      startDate = new Date(year, 0, 1)
      endDate = new Date(year, 2, 31)
      break
    case 2:
      startDate = new Date(year, 3, 1)
      endDate = new Date(year, 5, 30)
      break
    case 3:
      startDate = new Date(year, 6, 1)
      endDate = new Date(year, 8, 30)
      break
    case 4:
      startDate = new Date(year, 9, 1)
      endDate = new Date(year, 11, 31)
      break
  }
  return { startDate, endDate }
}

export function formatDateToString(date: Date) {
  const year = date.getFullYear()
  const month = ('0' + (date.getMonth() + 1)).slice(-2)
  const day = ('0' + date.getDate()).slice(-2)
  return `${year}-${month}-${day}`
}

export function arraysEqual(a: string[] | undefined, b: string[] | undefined) {
  if (a === b) return true
  if (a == null || b == null) return false
  if (a.length !== b.length) return false
  const sortedA = [...a].sort()
  const sortedB = [...b].sort()
  return sortedA.every((value, index) => value === sortedB[index])
}

export function removeMarkdown(markdown: string) {
  return markdown
    .replace(/(\*\*|__)(.*?)\1/g, '$2') // **bold** or __bold__
    .replace(/(\*|_)(.*?)\1/g, '$2') // *italic* or _italic_
    .replace(/`([^`]*)`/g, '$1') // `code`
    .replace(/#+\s(.+)/g, '$1') // # headers
    .replace(/!\[.*?\]\(.*?\)/g, '') // remove images
    .replace(/\[.*?\]\(.*?\)/g, '$1') // links
    .replace(/>\s/g, '') // blockquotes
    .replace(/[-+*]\s/g, '') // lists
    .replace(/\n+/g, ' ') // line breaks
    .trim() // remove leading/trailing spaces
}

export function validateYouTubeUrl(url: string) {
  const regex =
    /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/(watch\?v=|embed\/|v\/|.+\?v=)?([^&=%?]{11})/
  return regex.test(url)
}

export function validateApplePodcastUrl(url: string) {
  // const regex = /^https:\/\/podcasts\.apple\.com\/us\/podcast\/[^/]+\/id\d+/
  const regex = /^https:\/\/podcasts\.apple\.com\/[a-z]{2}\/podcast\/[^/]+\/id\d+/

  return regex.test(url)
}

export function validatePodcastEpisodeUrl(url: string) {
  const result = url.split('?i=')

  return result.length === 2
}

export function validateWebsiteUrl(url: string) {
  const regex = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d+)?(\/\S*)?$/

  return regex.test(url)
}

// CSV 생성 시 한 컬럼에 들어갈 값에 콤마가 포함되는 경우 두개의 컬럼으로 나뉘지 않도록 방지하는 함수
export const escapeCsvValue = (value: string | number) => {
  if (typeof value === 'string' && value.includes(',')) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

// Ownership Data 관련 계산 함수
export const getShareChangeCount = (
  data: OwnershipTransactionHistoryData[],
  targetHolderName: string,
  reportDate: string
) => {
  const inputDate = new Date(reportDate)
  const currentDate = new Date()

  const threeMonthsAgo = new Date()
  threeMonthsAgo.setMonth(currentDate.getMonth() - 3)

  if (inputDate.getTime() < threeMonthsAgo.getTime()) {
    return 0
  }
  const holderData = data.filter((data) => data.holder_name === targetHolderName)
  const filteredData = holderData.filter((item) => {
    const transactionDate = new Date(item.transaction_date)
    return transactionDate >= threeMonthsAgo && transactionDate <= currentDate
  })
  if (filteredData) {
    const totalShares = filteredData.reduce((sum, item) => {
      return sum + (item.number_of_shares || 0) // number_of_shares가 없으면 0으로 처리
    }, 0)
    return totalShares
  } else {
    return 0
  }
}

export const getChangePercent = (
  data: OwnershipTransactionHistoryData[],
  targetHolderName: string,
  targetPosition: number,
  reportDate: string
) => {
  const sharedChangeCount = getShareChangeCount(data, targetHolderName, reportDate)
  if (sharedChangeCount) {
    const changeRate = (sharedChangeCount / targetPosition) * 100
    return changeRate.toFixed(2)
  } else {
    return 0
  }
}

export function convertKeysToUpperCase(obj: Record<string, any>): Record<string, any> {
  const result: Record<string, any> = {}

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const upperKey = key.toUpperCase() // 키를 대문자로 변환
      result[upperKey] = obj[key] // 새로운 키로 값 매핑
    }
  }

  return result
}

export function base62ToNumber(base62: string) {
  const BASE62_CHARS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'
  const BASE = 62

  let result = BigInt(0)
  for (let i = 0; i < base62.length; i++) {
    result = result * BigInt(BASE) + BigInt(BASE62_CHARS.indexOf(base62[i]))
  }
  return result
}

export function numberToText(number: bigint) {
  let result = ''
  while (number > 0) {
    result = String.fromCharCode(Number(number % BigInt(256))) + result
    number = number / BigInt(256)
  }
  return result
}

export function decodeBase62(base62: string) {
  return numberToText(base62ToNumber(base62))
}

function expandRange(range: string): number[] {
  const [start, end] = range.split('-').map(Number)
  if (start <= end) {
    return Array.from({ length: end - start + 1 }, (_, i) => start + i)
  }
  return []
}

export function getReferencesInMessage(
  userMessage: Message,
  references?: Reference[] | NewsReference[],
  referenceMode?: ReferenceMode
) {
  const assistantMessage = userMessage?.child_message?.content
  if (references && references.length > 0 && assistantMessage) {
    let refIdxList: number[] = []
    assistantMessage.replace(REFERENCE_IDX_REGEX, (_, p1: string): string => {
      const idxList = p1
        .replace(/\s+/g, '')
        .split(',')
        .flatMap((idx) => {
          if (idx.includes('-')) {
            return expandRange(idx)
          } else {
            return [Number(idx)]
          }
        })
      refIdxList = refIdxList.concat(idxList)
      return ''
    })

    const uniqRefIdxList = Array.from(new Set(refIdxList))
    if (referenceMode === ReferenceMode.Search || referenceMode === ReferenceMode.IntegrateSearch) {
      return (references as Reference[]).filter((ref) => uniqRefIdxList.includes(ref.idx))
    } else {
      return (references as NewsReference[]).filter((ref) => uniqRefIdxList.includes(ref.position))
    }
  } else {
    return []
  }
}

export function getRmsReferencesInMessage(userMessage: Message, references?: RmsReference[]) {
  const assistantMessage = userMessage?.child_message?.content
  if (references && references.length > 0 && assistantMessage) {
    let refIdxList: number[] = []
    assistantMessage.replace(REFERENCE_IDX_REGEX, (_, p1: string): string => {
      const idxList = p1
        .replace(/\s+/g, '')
        .split(',')
        .flatMap((idx) => {
          if (idx.includes('-')) {
            return expandRange(idx)
          } else {
            return [Number(idx)]
          }
        })
      refIdxList = refIdxList.concat(idxList)
      return ''
    })

    const uniqRefIdxList = Array.from(new Set(refIdxList))
    return (references as RmsReference[]).filter((ref) => uniqRefIdxList.includes(ref.idx))
  }
}

export const getInferenceReferencesInMessage = (
  userMessage: Message,
  references?: InferenceReference[]
) => {
  const assistantMessage = userMessage?.child_message?.content
  if (references && references.length > 0 && assistantMessage) {
    let refIdxList: string[] = []
    assistantMessage.replace(/\[\s*([\da-zA-Z,\s,-]+)\s*\]/g, (match, p1) => {
      const idxList = p1
        .replace(/\s+/g, '')
        .split(',')
        .flatMap((idx: string) => {
          if (idx.includes('-')) {
            return expandRange(idx)
          } else {
            return [idx.trim()]
          }
        })
      refIdxList = refIdxList.concat(idxList)
      return ''
    })

    const uniqRefIdxList = Array.from(new Set(refIdxList))
    return (references as InferenceReference[]).filter((ref) => uniqRefIdxList.includes(ref.ref_id))
  }
}

export const getOnyxReferencesInMessage = (
  assistantMessage: string,
  references: InferenceReference[] | RmsV2Reference[]
) => {
  if (references && references.length > 0 && assistantMessage) {
    let refIdxList: string[] = []
    assistantMessage.replace(/\[\s*([\da-zA-Z,\s,-]+)\s*\]/g, (match, p1) => {
      const idxList = p1
        .replace(/\s+/g, '')
        .split(',')
        .flatMap((idx: string) => {
          if (idx.includes('-')) {
            return expandRange(idx)
          } else {
            return [idx]
          }
        })
      refIdxList = refIdxList.concat(idxList)
      return ''
    })
    const uniqRefIdxList = Array.from(new Set(refIdxList))
    return (references as RmsV2Reference[]).filter((ref) => uniqRefIdxList.includes(ref.ref_id))
  }
}

export function convertMarketCap(
  row: { content: Record<string, number | null> },
  column: { id: string }
): string {
  if (!row || !column) return 'NA'

  const value = row.content[column.id]
  const billion = 1_000_000_000 // 1 Billion

  return value && value > 0 ? `$${(value / billion).toFixed(1)}B` : 'NA'
}

export function convertMarketCapWithNumber(number: number) {
  const billion = 1_000_000_000 // 1 Billion
  return number > 0 ? `$${(number / billion).toFixed(2)}B` : 'N/A' // 소수점 2자리로 고정
}

export function truncateMarkdownString(input: string, maxLength: number) {
  const regex = /\[[\d,\s]+\]/g // 패턴: [숫자, 숫자,...]

  // 해당 문자열이 특정 길이를 초과하지 않으면 그대로 반환
  if (input.length <= maxLength) {
    return input
  }

  // 문자열을 잘라내고 말줄임 처리 추가
  let truncated = input.slice(0, maxLength)

  // 정규식을 사용해 잘린 문자열에서 패턴을 탐색 (matchAll 미사용)
  let match
  while ((match = regex.exec(input)) !== null) {
    const start = match.index
    const end = match.index + match[0].length

    // 잘린 범위 안에 패턴이 포함되면 해당 패턴 전체를 제거
    if (start < maxLength && end > maxLength) {
      truncated = truncated.slice(0, start)
    }
  }

  return truncated.trim() + '...'
}

export function formatTableColumnQuestionKey(key: string) {
  return key
    .split('_') // 언더스코어로 나누기
    .map(
      (word, index) =>
        index === 0
          ? word.charAt(0).toUpperCase() + word.slice(1) // 첫 단어는 대문자로
          : word // 나머지는 그대로
    )
    .join(' ') // 공백으로 결합
}

export function getDisplayDocumentType(type: ReferenceDocumentType) {
  let displayName = ''
  switch (type) {
    case ReferenceDocumentType.EarningsCall:
      displayName = 'Call Transcript'
      break
    case ReferenceDocumentType.SellSide:
      displayName = 'Broker Report'
      break
    case ReferenceDocumentType.Slides:
      displayName = 'IR Slides'
      break
    case ReferenceDocumentType.UploadedFile:
      displayName = 'Attachment'
      break
    default:
      displayName = 'Filings'
  }

  return displayName
}

export function getBrokerageName(name: string) {
  let displayName = ''
  switch (name) {
    case 'GS':
      displayName = 'Goldman Sachs'
      break
    case 'UBS':
      displayName = 'UBS'
      break
    case 'CITI':
      displayName = 'Citi'
      break
    default:
      displayName = 'Broker Report'
  }

  return displayName
}

export const replaceHashes = (
  markdownText: string,
  referencesData: InferenceReference[]
): string => {
  // ref_id를 소문자로 변환하여 매핑 객체 생성
  const mapping = referencesData.reduce<Record<string, number>>((acc, cur) => {
    acc[cur.ref_id.toLowerCase()] = cur.idx
    return acc
  }, {})

  // 대괄호 패턴과 소괄호 패턴 모두를 매칭하도록 정규식 작성
  // const regex = /(?:\[(?:hash:)?([\da-fA-F,\s]+)\])|(?:\((?:hash:)?([\da-fA-F,\s]+)\))/g
  // const regex =
  //   /(?:\[(?:(?:google|news|vdb):\s*)?([\da-fA-F,\s]+)\])|(?:\((?:(?:google|news|vdb):\s*)?([\da-fA-F,\s]+)\))/g
  const regex =
    /(?:\[(?:(?:google|news|vdb):\s*)?([\da-zA-Z,\s]+)\])|(?:\((?:(?:google|news|vdb):\s*)?([\da-zA-Z,\s]+)\))/g

  return markdownText.replace(regex, (match, group1, group2) => {
    const innerContent = group1 || group2
    const hashes = innerContent.split(',').map((str: string) => str.trim().toLowerCase())

    // mapping에 존재하는 해시값만 필터링한 후 매핑된 idx로 치환
    const replaced = hashes
      .filter((hash: string) => Object.prototype.hasOwnProperty.call(mapping, hash))
      .map((hash: string) => mapping[hash])
    // 치환할 값이 하나도 없으면 해당 패턴을 제거
    if (replaced.length === 0) {
      return ''
    }

    // 치환한 값을 다시 콤마와 공백으로 연결하여 대괄호로 감싸서 반환
    return `[${replaced.join(', ')}]`
  })
}

interface ReplaceHashOnyxProps {
  markdownText: string
  referencesData: RmsV2Reference[]
  isSkeleton?: boolean
}

export const replaceHashOnyx = ({
  markdownText,
  referencesData,
  isSkeleton = true,
}: ReplaceHashOnyxProps): string => {
  // ref_id를 소문자로 변환하여 매핑 객체 생성 (ref_id가 존재하는지 확인)
  const mapping = referencesData.reduce<Record<string, number>>((acc, cur) => {
    if (cur.ref_id && typeof cur.ref_id === 'string') {
      acc[cur.ref_id.trim().toLowerCase()] = cur.idx
    }
    return acc
  }, {})

  // 정수만 매칭하는 정규식 (길이 제한 없음)
  const validPatternRegex = /^\d+$/

  // 대괄호 안의 정수만 찾는 정규식
  const regex = /\[\s*(\d+)\s*\]/g

  return markdownText.replace(regex, (match, innerContent) => {
    const trimmedContent = innerContent.trim()

    // 빈 문자열인 경우 빈 문자열 반환
    if (!trimmedContent) {
      return ''
    }

    // 콤마로 분리하여 각 해시값 처리
    const hashes = trimmedContent
      .split(',')
      .map((str: string) => str.trim())
      .filter((hash: string) => hash.length > 0) // 빈 문자열 제거

    // 유효한 패턴만 필터링하고 mapping에 존재하는 해시값만 치환
    const replaced = hashes
      .filter((hash: string) => validPatternRegex.test(hash)) // 유효한 패턴인지 확인
      .filter((hash: string) => Object.prototype.hasOwnProperty.call(mapping, hash.toLowerCase()))
      .map((hash: string) => mapping[hash.toLowerCase()])

    // 치환할 값이 하나도 없으면 원래 매치된 문자열 그대로 반환
    if (replaced.length === 0) {
      if (isSkeleton) {
        return '[skeleton]'
      } else {
        return ''
      }
    }

    // 치환한 값을 다시 콤마와 공백으로 연결하여 대괄호로 감싸서 반환
    return `[${replaced.join(', ')}]`
  })
}

export const formatCellValue = (
  row: { content: Record<string, string | number | null> },
  column: { id: string; data_type: ColumnType }
): string => {
  const value = row.content[column.id]

  if (value === null || value === undefined) return '-'

  const stringValue = value.toString()

  if (column.data_type === 'float') {
    // 소수점 앞부분과 뒷부분 분리
    const [integerPart, decimalPart = '00'] = stringValue.split('.')
    // 정수 부분은 숫자로 변환하여 로케일 형식으로 처리
    const formattedInteger = Number(integerPart).toLocaleString()
    // 소수점 뒷부분은 2자리로 고정
    const fixedDecimal = decimalPart.padEnd(2, '0').slice(0, 2)
    return `${formattedInteger}.${fixedDecimal}`
  }

  // 문자열을 숫자로 변환
  const numberValue = Number(value)
  // 숫자로 변환이 불가능한 경우 그대로 리턴
  if (isNaN(numberValue)) {
    return String(value)
  }
  // 기본은 정수형으로 로케일 형식 반환
  return numberValue.toLocaleString()
}

export function getPeriodOptions() {
  const currentYear = new Date().getFullYear()
  const options = []

  // 올해부터 10년 전까지 순회 (예: 현재가 2025면 2025 ~ 2015)
  for (let year = currentYear; year >= currentYear - 10; year--) {
    options.push(`FY ${year}`)
    options.push(`H1 ${year}`)
    options.push(`H2 ${year}`)
    options.push(`Q4 ${year}`)
    options.push(`Q3 ${year}`)
    options.push(`Q2 ${year}`)
    options.push(`Q1 ${year}`)
  }

  return options
}

export function getPeriodIntervalType(period: string) {
  if (period.includes('Q')) {
    return 'quarterly'
  }
  if (period.includes('H')) {
    return 'semi-annual'
  }
  return 'annual'
}

export async function createPresignedUrl(
  fileKey: string,
  mode?: PresignedUrlMode
): Promise<string> {
  try {
    // URL 파라미터 생성
    const params = new URLSearchParams({
      fileKey: fileKey,
    })

    if (mode) {
      params.append('mode', mode)
    }

    const url = `/api/getPresignedUrl?${params.toString()}`
    const response = await fetch(url)

    // 응답 상태 확인
    if (!response.ok) {
      throw new Error(`Failed to create presigned URL: ${response.status} ${response.statusText}`)
    }

    const data = await response.json()

    // 응답 데이터 검증
    if (!data.signedUrl) {
      throw new Error('No signed URL in response')
    }

    return data.signedUrl
  } catch (error) {
    console.error('Error creating presigned URL:', error)
    throw error
  }
}

export const formatMetricValue = (
  row: { content: Record<string, string | number | null> },
  column: { id: string; metadata?: { metric?: { unit?: string } } }
): string => {
  const value = row.content[column.id]

  if (value === null || value === undefined) return 'NA'

  const numberValue = Number(value)
  if (isNaN(numberValue)) return String(value)

  const unit = column.metadata?.metric?.unit

  // unit이 'bn'인 경우 '$12,434.5B' 형태로 표시
  if (unit === 'bn') {
    return `$${numberValue.toLocaleString('en-US', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    })}B`
  }

  // unit이 'mn'인 경우 '$12,434.5M' 형태로 표시
  if (unit === 'mn') {
    return `$${numberValue.toLocaleString('en-US', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    })}M`
  }

  // unit이 '$'인 경우 천단위 콤마와 소수점 둘째자리까지 포맷팅
  if (unit === '$') {
    return `$${numberValue.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`
  }

  // 그 외의 경우 기본 포맷팅
  return numberValue.toLocaleString()
}

/**
 * Generate time options array with 15-minute intervals
 * @param startHour - The hour to start from (default: 0)
 * @param endHour - The hour to end at (default: 24)
 * @param interval - The interval between minutes (default: 15)
 * @returns An array of time options in the format of "HH:MM AM/PM"
 */
export const generateTimeOptions = ({
  startHour = 0,
  endHour = 24,
  interval = 15,
}: {
  startHour?: number
  endHour?: number
  interval?: number
}): string[] => {
  const options: string[] = []

  for (let hour = startHour; hour < endHour; hour++) {
    for (let minute = 0; minute < 60; minute += interval) {
      const period = hour < 12 ? 'AM' : 'PM'
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
      const displayMinute = minute.toString().padStart(2, '0')
      options.push(`${displayHour}:${displayMinute} ${period}`)
    }
  }

  return options
}

export const getSearchProcessValue = (searchProcess: SearchProcessItem[], type: string) => {
  const item = searchProcess.find((item) => item.type === type)
  return item?.value
}

export const checkTabCompletion = (batchResults: any, workflows: string[]): boolean => {
  if (!batchResults) return false

  return workflows.every((workflowName) => {
    const section = batchResults[workflowName]
    if (!section) return false
    return section.status === 'completed' || section.status === 'failed'
  })
}

// 탭 표시 가능 여부 체크 (하나라도 완료/실패)
export const canShowTab = (batchResults: any, workflows: string[]): boolean => {
  if (!batchResults) return false

  return workflows.some((workflowName) => {
    const section = batchResults[workflowName]
    if (!section) return false
    return section.status === 'completed' || section.status === 'failed'
  })
}

// 탭이 완전히 완료되었는지 체크 (모든 워크플로우가 완료/실패)
export const checkTabFullyComplete = (batchResults: any, workflows: string[]): boolean => {
  if (!batchResults) return false

  return workflows.every((workflowName) => {
    const section = batchResults[workflowName]
    if (!section) return false
    return section.status === 'completed' || section.status === 'failed'
  })
}

export function getCurrency(
  currencyInfo: CurrencyInfo[] | undefined,
  fieldCurrencyInfo: FieldCurrencyInfo | undefined
): string | undefined {
  // 1. currency_info에서 먼저 찾기
  if (currencyInfo && currencyInfo.length > 0 && currencyInfo[0]?.currency) {
    return currencyInfo[0].currency
  }

  // 2. field_currency_info에서 가장 빈번한 currency 찾기
  if (!fieldCurrencyInfo) return undefined

  const allCurrencies = Object.values(fieldCurrencyInfo)
    .flatMap((fields) => Object.values(fields))
    .filter(Boolean)

  if (allCurrencies.length === 0) return undefined

  const currencyCount = allCurrencies.reduce<Record<string, number>>((acc, currency) => {
    acc[currency] = (acc[currency] || 0) + 1
    return acc
  }, {})

  return Object.entries(currencyCount).sort((a, b) => b[1] - a[1])[0]?.[0]
}
